import { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, RefObject, useEffect, useRef, useState } from "react";

/* Min/max panel height for drag-resize. Max follows the current window height so resizing
   the window mid-drag still fits. */
const PANEL_MIN_HEIGHT_PX = 240;
const PANEL_MAX_HEIGHT_INSET_PX = 96;
const GRABBER_KEYBOARD_STEP_PX = 24;
const PANEL_FALLBACK_HEIGHT_PX = 512;

interface PanelResizeResult {
  readonly panelRef: RefObject<HTMLDialogElement>;
  readonly panelHeight: number | null;
  readonly minHeight: number;
  readonly onGrabberPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onGrabberKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Drag- and keyboard-resize for the floating chat panel. Tracks the panel height and clamps it
 * between a fixed floor and the current viewport height.
 */
export const usePanelResize = (): PanelResizeResult => {
  const panelRef = useRef<HTMLDialogElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
    };
  }, []);

  const resizePanel = (nextHeight: number) => {
    const maxHeight = window.innerHeight - PANEL_MAX_HEIGHT_INSET_PX;
    setPanelHeight(Math.min(Math.max(nextHeight, PANEL_MIN_HEIGHT_PX), maxHeight));
  };

  const onGrabberPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const strip = e.currentTarget;
    dragCleanupRef.current?.();
    strip.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startHeight = panelRef.current?.offsetHeight ?? PANEL_FALLBACK_HEIGHT_PX;

    const onPointerMove = (evt: PointerEvent) => {
      resizePanel(startHeight + (startY - evt.clientY));
    };

    const cleanup = () => {
      strip.removeEventListener("pointermove", onPointerMove);
      strip.removeEventListener("pointerup", onPointerEnd);
      strip.removeEventListener("pointercancel", onPointerEnd);
      strip.removeEventListener("lostpointercapture", onLostPointerCapture);
      if (dragCleanupRef.current === cleanup) dragCleanupRef.current = null;
    };

    const onLostPointerCapture = () => {
      cleanup();
    };

    const onPointerEnd = (evt: PointerEvent) => {
      if (strip.hasPointerCapture(evt.pointerId)) strip.releasePointerCapture(evt.pointerId);
      cleanup();
    };

    strip.addEventListener("pointermove", onPointerMove);
    strip.addEventListener("pointerup", onPointerEnd);
    strip.addEventListener("pointercancel", onPointerEnd);
    strip.addEventListener("lostpointercapture", onLostPointerCapture);

    dragCleanupRef.current = cleanup;
  };

  const onGrabberKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const currentHeight = panelRef.current?.offsetHeight ?? panelHeight ?? PANEL_FALLBACK_HEIGHT_PX;
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        resizePanel(currentHeight + GRABBER_KEYBOARD_STEP_PX);
        break;
      case "ArrowDown":
        e.preventDefault();
        resizePanel(currentHeight - GRABBER_KEYBOARD_STEP_PX);
        break;
      case "Home":
        e.preventDefault();
        resizePanel(PANEL_MIN_HEIGHT_PX);
        break;
      case "End":
        e.preventDefault();
        resizePanel(window.innerHeight - PANEL_MAX_HEIGHT_INSET_PX);
        break;
      default:
    }
  };

  return { panelRef, panelHeight, minHeight: PANEL_MIN_HEIGHT_PX, onGrabberPointerDown, onGrabberKeyDown };
};
